import React, { useState, useEffect, useCallback } from "react";

// recoil
import { useRecoilValue, useSetRecoilState } from "recoil";
import { userCredentials, routeInfo } from "recoilState/GlobalState";

//Services
import { usuarioLugarHorarioGet } from "services/services-mia/miakey";

// utils
import XGrid from "components/UI/Grid/XGrid";
import { HeaderButton, Buttons } from "components";
import { Add, Cached, TableChartOutlined, Schedule, ArrowUpward, ArrowDownward } from "@mui/icons-material";

import Modal from "components/UI/Modal/Modal";
import { CambiarEstadoModal, EstadoModal, InsertModal, RangoModal } from "./Actions";

// consts
import { dashboardAccesos as titles } from "consts/titulos-de-tablas";
import { ACTIONS } from "consts/Actions";
import { Routes } from "consts/Routes-App";

// styles
import s from "styles/pages/Pages.module.scss"

export default function AccesoDetails({
    usuario,
    service,
    onUpdate,
}) {
    const usuarioId = usuario.usuarioId
    const merchantServicioUsuarioId = usuario.id
    const [modalDetails, setModalDetails] = useState({ isOpen: false });
    const [isLoading, setIsLoading] = useState(true);
    const [rows, setRows] = useState([]);
    const [rowSelected, setRowSelected] = useState({ status: false, row: {} })

    const credentials = useRecoilValue(userCredentials);
    const setRoute = useSetRecoilState(routeInfo);

    const updateList = useCallback(async () => {
        setIsLoading(true);
        setRows([]);
        const { result } = await usuarioLugarHorarioGet({
            merchantServicioUsuarioId
        }, credentials);
        setRows(result.map((item) => {
            const {
                gateUsuarioLugarHorarioId,
                usuarioNombres,
                usuarioApellidos,
                gateUsuarioLugarHorarioStatus,
                gateUsuarioLugarHorarioFecha,
                gateHorarioId,
                gateHorarioNombre,
                gateHorarioDescripcion,
                gateZonaId,
                gateLugarId,
                gateLugarNombre,
                gateZonaNombre,
                gateUsuarioLugarHorarioVersion,
            } = item;

            return {
                id: gateUsuarioLugarHorarioId,
                gateUsuarioLugarHorarioId,
                usuarioNombres,
                usuarioApellidos,
                gateUsuarioLugarHorarioStatus,
                gateUsuarioLugarHorarioFecha,
                gateHorarioId,
                gateHorarioNombre,
                gateHorarioDescripcion,
                gateZonaId,
                gateLugarId,
                gateLugarNombre,
                gateZonaNombre,
                gateUsuarioLugarHorarioVersion,
                usuario: `${usuarioNombres} ${usuarioApellidos}`,
                [titles[0]]: gateZonaNombre,
                [titles[1]]: gateLugarNombre,
                [titles[2]]: gateHorarioNombre,
                [titles[3]]: gateHorarioDescripcion,
                [titles[4].field]: gateUsuarioLugarHorarioStatus === "A" ? "Alta" : "Baja",
                [titles[5]]: gateUsuarioLugarHorarioFecha,
            }
        }));
        setIsLoading(false);
        onUpdate?.();
    }, [credentials, merchantServicioUsuarioId, onUpdate]);

    useEffect(() => {
        setRoute(Routes.usuariosAccesos)
    }, [setRoute])

    useEffect(() => {
        updateList()
    }, [service, updateList]);

    const activeGateLugarIds = rows
        .filter((r) => r.gateUsuarioLugarHorarioStatus === "A")
        .map((r) => r.gateLugarId);

    const handleClick = ({ procede, title, targetStatus }) => {
        setModalDetails({ title, procede, targetStatus, isOpen: true });
    };

    const closeModal = () => {
        setModalDetails({ isOpen: false });
    };

    return (
        <main className={s.main}>
            <Modal onClose={closeModal} open={modalDetails.isOpen} title={modalDetails.title}>
                {modalDetails.procede === ACTIONS.INSERT && (
                    <InsertModal
                        usuarioId={usuarioId}
                        merchantServicioUsuarioId={merchantServicioUsuarioId}
                        activeGateLugarIds={activeGateLugarIds}
                        updateList={updateList}
                        closeModal={closeModal}
                    />
                )}
                {modalDetails.procede === ACTIONS.UPDATE && (
                    <CambiarEstadoModal
                        selectedRow={rowSelected.row}
                        targetStatus={modalDetails.targetStatus}
                        usuarioId={usuarioId}
                        merchantServicioUsuarioId={merchantServicioUsuarioId}
                        updateList={updateList}
                        closeModal={closeModal}
                    />
                )}
                {modalDetails.procede === ACTIONS.DETAIL && (
                    <RangoModal
                        horarioId={rowSelected.row.gateHorarioId}
                        service={service}
                        closeModal={closeModal}
                    />
                )}
                {modalDetails.procede === ACTIONS.GET && (
                    <EstadoModal
                        accesoDetailId={rowSelected.row.gateUsuarioLugarHorarioId}
                        service={service}
                        closeModal={closeModal}
                        selectedRow={rowSelected.row}
                        usuarioId={usuarioId}
                        merchantServicioUsuarioId={merchantServicioUsuarioId}
                    />
                )}
            </Modal>
            <div className={s.actions}>
                <Buttons>
                    <HeaderButton
                        text={"Actualizar"}
                        onClick={updateList}
                        disabled={rowSelected.row.id}
                    >
                        <Cached />
                    </HeaderButton>
                    <HeaderButton
                        text={"Histórico de Estados"}
                        handleClick={handleClick}
                        data={{
                            title: `Historial de Horarios - ${rowSelected.row.gateLugarNombre} - ${rowSelected.row.usuario}`,
                            procede: ACTIONS.GET
                        }}
                        disabled={!rowSelected.row.id}
                    >
                        <TableChartOutlined />
                    </HeaderButton>
                    <HeaderButton
                        text={"Detalles de Horario"}
                        handleClick={handleClick}
                        data={{
                            title: `Detalles de Horario - ${rowSelected.row.gateHorarioNombre ?? ""}`,
                            procede: ACTIONS.DETAIL
                        }}
                        disabled={!rowSelected.row.id}
                    >
                        <Schedule />
                    </HeaderButton>
                    <HeaderButton
                        text={"Agregar"}
                        handleClick={handleClick}
                        data={{
                            title: `Agregar Acceso - ${usuario.usuarioNombreCompleto ?? ""}`,
                            procede: ACTIONS.INSERT
                        }}
                        disabled={rowSelected.row.id}
                    >
                        <Add />
                    </HeaderButton>
                    {(rowSelected.row.gateUsuarioLugarHorarioStatus === "A" || !rowSelected.row.id) && (
                        <HeaderButton
                            text={"Dar de Baja"}
                            handleClick={handleClick}
                            data={{
                                title: `Dar de Baja Acceso - ${usuario.usuarioNombreCompleto ?? ""}`,
                                procede: ACTIONS.UPDATE,
                                targetStatus: "B",
                            }}
                            disabled={!rowSelected.row.id}
                        >
                            <ArrowDownward />
                        </HeaderButton>
                    )}
                    {rowSelected.row.gateUsuarioLugarHorarioStatus === "B" && (
                        <HeaderButton
                            text={"Dar de Alta"}
                            handleClick={handleClick}
                            data={{
                                title: `Dar de Alta Acceso - ${usuario.usuarioNombreCompleto ?? ""}`,
                                procede: ACTIONS.UPDATE,
                                targetStatus: "A",
                            }}
                        >
                            <ArrowUpward />
                        </HeaderButton>
                    )}
                </Buttons>
            </div>
            <div className={s.container}>
                <XGrid
                    rows={rows}
                    titles={titles}
                    loading={isLoading}
                    secondarySelected={rowSelected}
                    setSecondarySelected={setRowSelected}
                />
            </div>
        </main>
    );
}
